import {
  ErrorHandler,
  inject,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  RouteReuseStrategy,
  Router,
  RouterModule,
  TitleStrategy,
} from '@angular/router';
import { DatePipe, registerLocaleData } from '@angular/common';

import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client';

import * as Sentry from '@sentry/angular';

import { APP_ROUTES } from './app.routes';

import { AppComponent } from './app.component';

import { environment } from 'src/environments/environment';

import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { appReducer } from 'src/store/app/app.reducers';
import { userReducer } from 'src/store/user/user.reducers';
import { coreDataReducer } from 'src/store/core-data/core-data.reducers';
import { enterprisesReducer } from 'src/store/enterprises/enterprises.reducers';
import { partnerEnterpriseReducer } from 'src/store/partner-enterprise/partner-enterprise.reducers';

import { ToastComponent } from 'lipp-components';
import { SplashScreenModule } from 'src/components/splash-screen/splash-screen.module';

import { CustomTitleStrategy } from 'src/handlers/title.strategy';
import { AppRouteReuseStrategy } from 'src/handlers/route-reuse.strategy';

import localeDe from '@angular/common/locales/de';

registerLocaleData(localeDe);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(APP_ROUTES),
    StoreModule.forRoot({
      app: appReducer,
      coreData: coreDataReducer,
      user: userReducer,
      enterprises: enterprisesReducer,
      partnerEnterprise: partnerEnterpriseReducer,
    }),
    !environment.production ? StoreDevtoolsModule.instrument({}) : [],
    ToastComponent,
    SplashScreenModule,
  ],
  providers: [
    DatePipe,
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    provideNamedApollo(() => {
      const httpLink = inject(HttpLink);

      return {
        default: {
          link: httpLink.create({
            uri: `${environment.lippConnectUrl}/POSSystemManagement/graphql`,
          }),
          cache: new InMemoryCache(),
        },
        bmd: {
          link: httpLink.create({
            uri: `${environment.lippConnectUrl}/Fibu/graphql`,
          }),
          cache: new InMemoryCache(),
        },
        cashJournal: {
          link: httpLink.create({
            uri: `${environment.lippConnectUrl}/CashJournal/graphql`,
          }),
          cache: new InMemoryCache(),
        },
        posTransaction: {
          link: httpLink.create({
            uri: `${environment.lippConnectUrl}/POSTransactionManagement/graphql`,
          }),
          cache: new InMemoryCache(),
        },
      };
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        logErrors: false,
        extractor(error: Error, defaultExtractor) {
          if (
            !(
              error instanceof Error &&
              error.message.includes(
                'ExpressionChangedAfterItHasBeenCheckedError',
              )
            )
          ) {
            console.error(error);
          }

          return defaultExtractor(error);
        },
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
